/**
 * Décors planétaires (phases D et E) :
 * - planète vue d'orbite : sphère texturée éclairée par le soleil du décor, couche de nuages, halo d'atmosphère ;
 * - sol d'une zone de surface : maillage du champ de hauteurs de la collision (ground.ts), couleurs de sommets
 *   selon l'altitude et la pente, texture de détail répétée.
 */
import { Color3, Constants, FresnelParameters, Mesh, MeshBuilder, StandardMaterial, Texture, TransformNode, VertexData, type Scene } from '@babylonjs/core';
import { markSolid } from './postfx';
import { assetUrl } from '../assets';
import type { ZoneData } from '../data';
import { groundField, groundVertex } from '../sim/world/ground';

type PlanetData = NonNullable<ZoneData['planet']>;
type GroundData = NonNullable<ZoneData['ground']>;

export interface PlanetView {
  body: Mesh;
  clouds: Mesh;
  spin: number;
}

export function buildPlanet(scene: Scene, planet: PlanetData, parent: TransformNode): PlanetView {
  // Axe des pôles basculé vers l'horizon : depuis l'orbite, on voit l'équateur (continents, océans) et non la
  // calotte polaire. Le corps et les nuages tournent autour de cet axe.
  const pivot = new TransformNode('planet-pivot', scene);
  pivot.position.set(...planet.position);
  pivot.rotation.x = 1.25;
  pivot.parent = parent;
  const make = (name: string, radius: number) => {
    const mesh = MeshBuilder.CreateSphere(name, { diameter: radius * 2, segments: 96 }, scene);
    mesh.parent = pivot;
    mesh.isPickable = false;
    mesh.applyFog = false;
    return mesh;
  };
  const body = make('planet', planet.radius);
  // Éclairée par le soleil du décor (face jour, face nuit), réponse atténuée : les intensités sont réglées pour le
  // PBR et satureraient un matériau Standard.
  const surface = new StandardMaterial('planet-surface', scene);
  surface.diffuseTexture = new Texture(assetUrl(planet.texture), scene, false, false);
  surface.diffuseColor = new Color3(0.5, 0.5, 0.52);
  surface.specularColor = new Color3(0.05, 0.06, 0.08);
  surface.emissiveColor = new Color3(0.015, 0.02, 0.035);
  body.material = surface;
  const clouds = make('planet-clouds', planet.radius * 1.012);
  const cloudMat = new StandardMaterial('planet-clouds', scene);
  const cloudTex = new Texture(assetUrl(planet.clouds), scene, false, false);
  cloudTex.hasAlpha = true;
  cloudMat.diffuseTexture = cloudTex;
  cloudMat.useAlphaFromDiffuseTexture = true;
  cloudMat.diffuseColor = new Color3(0.55, 0.56, 0.58);
  cloudMat.specularColor = Color3.Black();
  cloudMat.alpha = 0.95;
  cloudMat.disableDepthWrite = true;
  clouds.material = cloudMat;
  // Halo : sphère un peu plus grande, en mélange additif, noire au centre et colorée au bord (Fresnel).
  const halo = make('planet-atmosphere', planet.radius * 1.04);
  const haloMat = new StandardMaterial('planet-atmosphere', scene);
  haloMat.disableLighting = true;
  haloMat.emissiveColor = Color3.Black();
  haloMat.emissiveFresnelParameters = new FresnelParameters({ bias: 0, power: 2.5, leftColor: new Color3(...planet.atmosphere), rightColor: Color3.Black() });
  // Alpha < 1 : sans cela le matériau est jugé opaque et le mélange additif n'est pas appliqué.
  haloMat.alpha = 0.99;
  haloMat.alphaMode = Constants.ALPHA_ADD;
  haloMat.disableDepthWrite = true;
  halo.material = haloMat;
  return { body, clouds, spin: (planet.spinDeg * Math.PI) / 180 };
}

export function buildGround(scene: Scene, ground: GroundData, detail: Texture, parent: TransformNode): Mesh {
  const field = groundField(ground);
  const n = field.n;
  const positions: number[] = [];
  const colors: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const low = new Color3(...ground.low), high = new Color3(...ground.high);
  const at = (row: number, col: number) => field.heights[row + col * n]!;
  const hMin = Math.min(...field.heights), hMax = Math.max(...field.heights);
  for (let row = 0; row < n; row++) {
    for (let col = 0; col < n; col++) {
      const [x, z] = groundVertex(field, row, col);
      const y = at(row, col);
      positions.push(x, y, z);
      // Pente (différences finies) : roche plus claire sur les flancs raides, herbe rase dans les creux.
      const dx = at(row, Math.min(n - 1, col + 1)) - at(row, Math.max(0, col - 1));
      const dz = at(Math.min(n - 1, row + 1), col) - at(Math.max(0, row - 1), col);
      const slope = Math.min(1, Math.hypot(dx, dz) / (4 * (field.size / (n - 1))));
      const t = Math.min(1, Math.max(0, (y - hMin) / Math.max(1, hMax - hMin) * 0.8 + slope * 0.9));
      const c = Color3.Lerp(low, high, t);
      colors.push(c.r, c.g, c.b, 1);
      uvs.push(x / 60, z / 60);
    }
  }
  for (let row = 0; row < n - 1; row++) {
    for (let col = 0; col < n - 1; col++) {
      const a = row * n + col, b = a + 1, c = a + n, d = c + 1;
      indices.push(a, b, c, b, d, c);
    }
  }
  const mesh = new Mesh('ground', scene);
  const vd = new VertexData();
  vd.positions = positions;
  vd.indices = indices;
  vd.colors = colors;
  vd.uvs = uvs;
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  vd.normals = normals;
  vd.applyToMesh(mesh);
  const mat = new StandardMaterial('ground', scene);
  mat.diffuseTexture = detail;
  mat.specularColor = new Color3(0.04, 0.04, 0.04);
  mat.diffuseColor = new Color3(0.42, 0.42, 0.42);
  mat.maxSimultaneousLights = 8;
  mesh.material = mat;
  mesh.parent = parent;
  mesh.isPickable = false;
  // Relief solide : reçoit (et porte) les ombres du soleil (postfx.ts).
  markSolid(mesh);
  mesh.freezeWorldMatrix();
  return mesh;
}
